import type { History } from "history";

import { captureException } from "./capture-exception";
import { waitForPendingMutations } from "./pending-mutations";

// Nearly every chunk imports the entry file, so a deploy renames almost all of
// them, and a tab left open across one cannot load the pages it hasn't visited
// yet. Such a tab reloads onto the new version instead, at the moment its user
// changes page anyway, once the saves that click started have finished.

// Browsers word a failed import differently, and Vite throws its own error for CSS.
const chunkLoadErrorPattern =
  /dynamically imported module|importing a module script failed|not a valid javascript mime type|unable to preload css/i;

export const isChunkLoadError = (error: unknown) =>
  chunkLoadErrorPattern.test((error as Error | null)?.message ?? "");

// A chunk can also fail on a plain network drop, so only an error marked here gets
// the page that says Cruitive has been updated.
const updateErrors = new WeakSet<object>();

export const markUpdateError = (error: unknown) => {
  updateErrors.add(error as object);
};

export const isUpdateError = (error: unknown) =>
  updateErrors.has(error as object);

// Not a crash, and every tab on a bad connection can send one, so these share a
// low-level issue that shows how often it happens.
export const reportChunkFailure = (error: unknown) => {
  captureException(error, {
    level: "info",
    fingerprint: ["chunk-load-failed"],
  });
};

const entryScriptSelector = 'script[type="module"][src^="/assets/"]';

// The entry's file name changes with every build. Returns undefined when the
// answer is unknown: offline, or in development, where there is no built entry.
export async function isNewVersionDeployed() {
  const runningEntry = document
    .querySelector(entryScriptSelector)
    ?.getAttribute("src");
  if (!runningEntry) {
    return undefined;
  }

  try {
    const response = await fetch("/", { cache: "no-store" });
    if (!response.ok) {
      return undefined;
    }
    const deployedEntry = new DOMParser()
      .parseFromString(await response.text(), "text/html")
      .querySelector(entryScriptSelector)
      ?.getAttribute("src");
    return deployedEntry ? deployedEntry !== runningEntry : undefined;
  } catch {
    return undefined;
  }
}

const lastReloadKey = "new-version-reloaded-at";
const minReloadInterval = 30_000;

// At most one reload per tab every 30 seconds, so a chunk that still fails after
// reloading shows the error page instead of reloading forever.
function mayReload() {
  try {
    const lastReload = Number(sessionStorage.getItem(lastReloadKey) ?? 0);
    return Date.now() - lastReload > minReloadInterval;
  } catch {
    return false;
  }
}

// Resolves false without reloading when a save fails or hangs, so the user keeps
// what they typed and sees the error.
export async function guardedReload(href?: string) {
  if (!mayReload()) {
    return false;
  }
  // Let the click that got here finish, including any save it starts.
  await new Promise((resolve) => setTimeout(resolve, 0));
  if (!(await waitForPendingMutations(10_000))) {
    return false;
  }

  try {
    sessionStorage.setItem(lastReloadKey, String(Date.now()));
  } catch {
    return false;
  }
  if (href) {
    window.location.assign(href);
  } else {
    window.location.reload();
  }
  return true;
}

// Holds the next change of page and loads it from the server instead. The page
// stays mounted until then, so inputs that save on blur still save. Query string
// changes stay in the app, because filters and tabs keep state in them.
function reloadOnNextPageChange(history: History) {
  let reloading = false;
  const unblock = history.block((location) => {
    if (location.pathname === history.location.pathname) {
      return undefined;
    }
    if (reloading) {
      return false;
    }
    if (!mayReload()) {
      unblock();
      return undefined;
    }

    reloading = true;
    void guardedReload(history.createHref(location)).then((reloaded) => {
      reloading = reloaded;
    });
    return false;
  });
}

const chunkRetryKey = "chunk-retry-reloaded";

// A chunk that failed with no new version behind it can fail the same way after the
// reload. One that only a dialog needs would then reload the page on every click, with
// nothing to say why. So this gets one reload per tab, and after that the error page.
export async function reloadToRetryChunk() {
  try {
    if (sessionStorage.getItem(chunkRetryKey)) {
      return false;
    }
    sessionStorage.setItem(chunkRetryKey, "1");
  } catch {
    return false;
  }

  const reloaded = await guardedReload();
  if (!reloaded) {
    sessionStorage.removeItem(chunkRetryKey);
  }
  return reloaded;
}

const checkInterval = 5 * 60_000;

// Checks every five minutes while the tab is visible, and whenever it becomes
// visible again, so a tab left open overnight picks up the new version at its
// first page change instead of breaking on a missing chunk.
export function watchForNewVersion(history: History) {
  let checking = false;
  let detected = false;

  const check = async () => {
    if (checking || detected || document.visibilityState !== "visible") {
      return;
    }
    checking = true;
    detected = (await isNewVersionDeployed()) === true;
    checking = false;
    if (detected) {
      reloadOnNextPageChange(history);
    }
  };

  setInterval(() => void check(), checkInterval);
  document.addEventListener("visibilitychange", () => void check());
}
